import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Mail, ArrowRight, Loader2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth, formatApiError } from "@/shared/context/AuthContext";
import { api } from "@/shared/lib/api";

const PROFILE_DISMISS_KEY = "link.account.profile-prompt.dismissed";
const VERIFY_DISMISS_KEY = "link.account.verify-prompt.dismissed";

/**
 * Two soft prompts surfaced at the top of /account, styled to match the
 * dusk-glass drawer aesthetic: translucent light-on-dark cards with
 * cream typography and orange kept only on icon chips + CTAs.
 */
const GLASS_SURFACE = {
  background: "var(--dusk-glass)",
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  borderRadius: 20,
  boxShadow: "var(--dusk-glass-border)",
};

export function AccountPrompts() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <>
      <EmailVerifyBanner user={user} />
      <ProfileCompletionCard user={user} />
    </>
  );
}

function EmailVerifyBanner({ user }) {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(VERIFY_DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", {});
      setSent(true);
      toast.success("Verification email sent.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't resend right now");
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(VERIFY_DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="p-4 flex items-start gap-3"
      style={GLASS_SURFACE}
      data-testid="account-verify-banner"
    >
      <span
        className="w-9 h-9 inline-flex items-center justify-center rounded-xl shrink-0"
        style={{ background: "rgba(237,101,0,0.12)", color: "#ed6500" }}
      >
        <Mail size={15} strokeWidth={2} />
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="font-semibold text-[14px] leading-tight"
          style={{ color: "var(--dusk-text)" }}
        >
          Verify your email
        </div>
        <div
          className="text-[12px] mt-0.5"
          style={{ color: "var(--dusk-text-muted)" }}
        >
          So we can send ride confirmations and pickup alerts to{" "}
          <span className="font-semibold" style={{ color: "var(--dusk-text)" }}>{user.email}</span>.
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={busy || sent}
          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] tracking-[0.2em] uppercase font-semibold disabled:opacity-60 transition-colors"
          style={{ background: "#ed6500", color: "#F9F7F4" }}
          data-testid="account-verify-resend"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> :
           sent ? <CheckCircle2 size={11} /> : <Mail size={11} />}
          {sent ? "Email sent" : "Resend email"}
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 -m-1 shrink-0 transition-colors"
        style={{ color: "var(--dusk-text-muted)" }}
        aria-label="Dismiss"
        data-testid="account-verify-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function ProfileCompletionCard({ user }) {
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(PROFILE_DISMISS_KEY) === "1"; }
    catch { return false; }
  });

  if (user.profile_complete || dismissed) return null;
  const missing = user.missing_profile_fields || [];
  if (missing.length === 0) return null;

  const friendly = missing.map((f) => ({
    first_name: "First name",
    last_name: "Last name",
    phone: "Phone number",
    home_address: "Home address",
  }[f] || f));

  const target = missing.includes("home_address") ? "/saved-places"
               : missing.includes("phone") ? "/profile"
               : "/profile";

  const dismiss = () => {
    try { localStorage.setItem(PROFILE_DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="p-4 flex items-start gap-3 relative overflow-hidden"
      style={GLASS_SURFACE}
      data-testid="account-profile-prompt"
    >
      <span
        className="w-9 h-9 inline-flex items-center justify-center rounded-xl shrink-0"
        style={{ background: "rgba(237,101,0,0.12)", color: "#ed6500" }}
      >
        <AlertCircle size={15} strokeWidth={2} />
      </span>
      <div className="flex-1 min-w-0 relative">
        <div
          className="font-semibold text-[14px] leading-tight"
          style={{ color: "var(--dusk-text)" }}
        >
          Complete your rider profile
        </div>
        <div
          className="text-[12px] mt-0.5"
          style={{ color: "var(--dusk-text-muted)" }}
        >
          Still missing: {friendly.join(", ")}.
        </div>
        <button
          type="button"
          onClick={() => nav(target)}
          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors"
          style={{ background: "#ed6500", color: "#F9F7F4" }}
          data-testid="account-profile-prompt-cta"
        >
          Finish setup <ArrowRight size={11} />
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 -m-1 shrink-0 relative transition-colors"
        style={{ color: "var(--dusk-text-muted)" }}
        aria-label="Dismiss"
        data-testid="account-profile-prompt-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
